import {
  AnchoredPanel,
  AnchoredPanelType,
  getResourceLifecycleStatus,
  LabelMetaItem,
  MetaItemGroupsLayout,
  MetaItemsGroupsDirection,
  stateT,
  Status,
} from "o4a-react";
import * as React from "react";
import { Spinner, Stack } from "@fluentui/react";
import apiClients from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { OciExaDbHomeLink } from "../../components/OciLinks/OciExaDbHomeLink";
import { metaItemLabelsGap, ttlOneMinCaching } from "../../constants/uiConstants";
import { parseId } from "../../helpers/idHelper";
import { getStatusInfo, optimizedRetryOption } from "../../helpers/resourceHelper";
import { AnalyticsDataPage } from "../../hooks/useAnalytics";
import { useQueryCall } from "../../hooks/useQueryCall";
import { AzureSubscriptionSummaryExt } from "../../hooks/useSubscriptions";
import { getAzureLocationName, getOciRegion } from "../../utils";
import { OperationActionProps } from "../OperationTypes";

export interface ExaDbViewDbHomeProps extends OperationActionProps {
  dbHomeId: string | undefined;
  subscriptions: AzureSubscriptionSummaryExt[];
  analytics: AnalyticsDataPage;
}

export const ExaDbViewDbHome = ({
  targetId,
  dbHomeId,
  subscriptions,
  location,
  onCancel,
  analytics,
}: ExaDbViewDbHomeProps): JSX.Element => {
  const idComps = parseId(dbHomeId);
  const resGroup = decodeURIComponent(idComps?.resourceGroup || "");
  const resourceName = decodeURIComponent(idComps?.resourceName || "");
  const subscription = subscriptions?.find(item => item.id === idComps?.subscriptionId);

  const { response, loading } = useQueryCall({
    wait: !dbHomeId,
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getDbHome,
    options: {
      args: {
        subscriptionId: idComps?.subscriptionId || "",
        resourceGroupName: resGroup,
        dbHomeName: resourceName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadExadbHome(),
      },
    },
  });

  const dbHome = response?.data;

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink resourceId={dbHomeId as string} />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(dbHome?.status || "")}
          status={getResourceLifecycleStatus(dbHome?.status || "")}
          statusInfo={getStatusInfo(
            dbHome?.status || "",
            dbHome?.lastOperationStatus || "",
            dbHome?.lastOperationStatusDetails || "",
            dbHome?.lifecycleDetails,
          )}
        />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.location()}>
        {getAzureLocationName(dbHome?.location || "")}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.subscription()}>
        <AzureSubscriptionLink resourceId={dbHomeId as string} subscriptionName={subscription?.name} />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.subscriptionId()}>
        {idComps?.subscriptionId}
      </LabelMetaItem>
    </Stack>,
    <Stack tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem label={Messages.labels.ociResource()}>
        <OciExaDbHomeLink
          id={dbHome?.id || ""}
          location={location}
          subscriptions={subscriptions}
          analytics={analytics}
        />
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.databaseVersion()}>
        {dbHome?.dbVersion || ""}
      </LabelMetaItem>
      <LabelMetaItem label={Messages.labels.databaseHomeLocation()}>
        {dbHome?.dbHomeLocation || ""}
      </LabelMetaItem>
    </Stack>,
  ];

  return (
    <AnchoredPanel
      anchorId={targetId}
      type={AnchoredPanelType.CUSTOM_WIDTH}
      customWidth={700}
      title={dbHome?.name || ""}
      isOpen
      onClose={onCancel}
    >
      {loading ? <Spinner label={Messages.common.loading()} />
        : (
          <MetaItemGroupsLayout direction={MetaItemsGroupsDirection.Vertical}>
            { essentialsMetaItemGroup }
          </MetaItemGroupsLayout>
        )}
    </AnchoredPanel>
  );
};

export const newExaDbViewDbHome = (
  props: ExaDbViewDbHomeProps,
): JSX.Element => (<ExaDbViewDbHome {...props} />);
